/**
 * AI Model 配置块 JSON Schema 定义
 */
export const aiModelSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  $id: 'https://o2world.ai/schemas/config-types/ai_model.json',
  title: 'AI Model 配置',
  description: 'AI Model（AI 模型）配置数据结构',
  type: 'object',
  required: ['model_id', 'provider', 'model_name'],
  properties: {
    model_id: {
      type: 'string',
      title: 'Model ID',
      description: 'AI Model 唯一标识符',
      pattern: '^model_[a-zA-Z0-9_]+$',
      minLength: 7,
      maxLength: 50,
      examples: ['model_doubao_pro', 'model_gpt4_chat', 'model_custom_npc'],
    },
    name: {
      type: 'string',
      title: '模型名称',
      description: '模型的显示名称',
      minLength: 1,
      maxLength: 100,
      examples: ['豆包 Pro 32K', 'GPT-4 Chat', '自定义 NPC 模型'],
    },
    description: {
      type: 'string',
      title: '模型描述',
      description: '模型的详细描述',
      minLength: 0,
      maxLength: 500,
    },
    provider: {
      type: 'string',
      title: '提供商',
      description: 'AI 模型的服务提供商',
      enum: ['doubao', 'openai', 'anthropic', 'azure', 'custom', '其他'],
    },
    model_name: {
      type: 'string',
      title: '模型名称',
      description: '提供商的模型标识',
      minLength: 1,
      maxLength: 100,
      examples: ['ep-20240507164303-lw6b', 'gpt-4-turbo', 'claude-3-opus-20240229'],
    },
    api_endpoint: {
      type: 'string',
      title: 'API 端点',
      description: 'API 请求的端点 URL',
      format: 'uri',
      maxLength: 500,
    },
    api_key: {
      type: 'string',
      title: 'API 密钥',
      description: 'API 认证密钥（加密存储）',
      maxLength: 200,
    },
    parameters: {
      type: 'object',
      title: '模型参数',
      description: '模型推理的参数配置',
      properties: {
        temperature: {
          type: 'number',
          title: '温度',
          description: '控制随机性（0-1）',
          minimum: 0,
          maximum: 1,
          default: 0.7,
        },
        max_tokens: {
          type: 'integer',
          title: '最大令牌数',
          description: '生成的最大令牌数',
          minimum: 1,
          maximum: 128000,
          default: 2000,
        },
        top_p: {
          type: 'number',
          title: 'Top P',
          description: '核采样参数',
          minimum: 0,
          maximum: 1,
          default: 0.9,
        },
        top_k: {
          type: 'integer',
          title: 'Top K',
          description: '保留的顶部 K 个令牌',
          minimum: 1,
          default: 40,
        },
        frequency_penalty: {
          type: 'number',
          title: '频率惩罚',
          description: '降低重复令牌的频率',
          minimum: -2,
          maximum: 2,
          default: 0,
        },
        presence_penalty: {
          type: 'number',
          title: '存在惩罚',
          description: '鼓励谈论新话题',
          minimum: -2,
          maximum: 2,
          default: 0,
        },
        stop: {
          type: 'array',
          title: '停止序列',
          description: '遇到这些令牌时停止生成',
          items: {
            type: 'string',
          },
        },
      },
    },
    system_prompt_template: {
      type: 'string',
      title: '系统提示词模板',
      description: 'AI 的系统提示词模板',
      minLength: 0,
      maxLength: 10000,
    },
    context_window: {
      type: 'integer',
      title: '上下文窗口',
      description: '模型支持的最大上下文长度',
      minimum: 1,
    },
    capabilities: {
      type: 'array',
      title: '能力',
      description: '模型支持的能力',
      items: {
        type: 'string',
        enum: ['对话', '角色扮演', '记忆', '多轮对话', '工具调用', '代码生成', '其他'],
      },
    },
    cost_per_1k_tokens: {
      type: 'object',
      title: '每千令牌成本',
      description: '使用成本（元/千令牌）',
      properties: {
        input: {
          type: 'number',
          title: '输入成本',
          minimum: 0,
        },
        output: {
          type: 'number',
          title: '输出成本',
          minimum: 0,
        },
      },
    },
    rate_limits: {
      type: 'object',
      title: '速率限制',
      description: 'API 速率限制',
      properties: {
        requests_per_minute: {
          type: 'integer',
          title: '每分钟请求数',
          minimum: 1,
        },
        tokens_per_minute: {
          type: 'integer',
          title: '每分钟令牌数',
          minimum: 1,
        },
      },
    },
    fallback_model: {
      type: 'string',
      title: '备用模型',
      description: '当前模型不可用时使用的备用模型 ID',
      pattern: '^model_[a-zA-Z0-9_]+$',
    },
    use_cases: {
      type: 'array',
      title: '使用场景',
      description: '模型适用的使用场景',
      items: {
        type: 'string',
        maxLength: 50,
        examples: ['NPC 对话', '任务生成', '剧情创作', '文本总结'],
      },
    },
    active: {
      type: 'boolean',
      title: '激活',
      description: '模型是否激活可用',
      default: true,
    },
    version: {
      type: 'string',
      title: '版本',
      description: '模型配置的版本',
      maxLength: 20,
    },
    metadata: {
      type: 'object',
      title: '元数据',
      description: '扩展元数据',
      additionalProperties: true,
    },
  },
  additionalProperties: false,
} as const

export type AiModelConfig = {
  model_id: string
  name?: string
  description?: string
  provider: string
  model_name: string
  api_endpoint?: string
  api_key?: string
  parameters?: {
    temperature?: number
    max_tokens?: number
    top_p?: number
    top_k?: number
    frequency_penalty?: number
    presence_penalty?: number
    stop?: string[]
  }
  system_prompt_template?: string
  context_window?: number
  capabilities?: string[]
  cost_per_1k_tokens?: {
    input?: number
    output?: number
  }
  rate_limits?: {
    requests_per_minute?: number
    tokens_per_minute?: number
  }
  fallback_model?: string
  use_cases?: string[]
  active?: boolean
  version?: string
  metadata?: Record<string, any>
}
